import { useTranslation } from "react-i18next";
import { SettingsSection } from "../app/SettingsSection";
import { setSetting } from "../app/settings";
import { useSession } from "../app/session";
import { useCoreDoc } from "../app/useCoreDoc";
import { C } from "../core/schema";
import { fairnessOf, type Fairness } from "../core/parts";
import { useDocSelector } from "../core/useDocSelector";
import { Select } from "../ui";

/**
 * *Random picks* (ADR 0037): what *Pick people at random* and *Pick subtasks at random* do, for every task in the
 * workspace — *Even turns* (the counts first, a draw among the equally owed) or *Pure chance* (a fresh draw each
 * round). The workspace's owner decides; people are only drawn in a shared workspace.
 */
export function FairnessSettings() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const workspaceId = useSession((s) => s.workspaceId);
  const { handle } = useCoreDoc();
  const fair = useDocSelector(handle, (doc) => fairnessOf(doc.getMap(C.settings).toJSON()));
  const current = session?.workspaces?.find((w) => w.id === workspaceId);
  if (!handle || !fair || current?.role !== "owner" || session?.profile?.kind === "kid") return null;
  const choice = (key: keyof Fairness) => (
    <label className="flex flex-wrap items-center gap-3 text-sm">
      <span className="w-40 shrink-0 text-[12.5px] text-neutral-500">{t(`fairness.${key}`)}</span>
      <Select
        value={fair[key]}
        disabled={handle.readOnly}
        onChange={(e) => setSetting(handle.doc, `fairness.${key}`, e.target.value)}
        aria-label={t(`fairness.${key}`)}
        data-testid={`fairness-${key}`}
      >
        <option value="even">{t("fairness.even")}</option>
        <option value="chance">{t("fairness.chance")}</option>
      </Select>
    </label>
  );
  return (
    <SettingsSection
      id="settings-fairness"
      intro={t("fairness.intro")}
      title={t("fairness.title")}
      testId="settings-fairness"
      folded
    >
      {current.shared && choice("people")}
      {choice("subtasks")}
      <p className="text-[12px] text-neutral-500">{t("fairness.hint")}</p>
    </SettingsSection>
  );
}
